import React from 'react';
import PropTypes from 'prop-types';

import { findDOMNode } from 'react-dom';
import cns from 'classnames';
import { getProp, setProp } from '../Utils/formHelpers';
import Validation from '../Validation';
import Helpers from '../Utils/nodeHelpers';
import Option from './Option';
import Divider from './Divider';
import doesNodeContainClick from '../Utils/doesNodeContainClick';
import { getTitle, getRandomId, Presentation } from '../Utils/baseComponentHelper';
import { FormWrapperPropTypes } from '../Form/withForm';
import EllipsisText from '../EllipsisText';
import useValidations from '../Hooks/useValidations';
import ValidationViewer from '../Validation/ValidationViewer';
import useActivation from '../Hooks/useActivation';

Option.displayName = 'Select.Option';

/**
Le select vengono utilizzate quando si ha la necessità di mostrare una serie di contenuti e
permettere all’utente di fare una scelta fra questi.
Le voci del menu possono essere disabilitate se non applicabili ad un certo contesto.
 */
class Select extends React.Component {
  static Option = Option;

  static Divider = Divider;

  constructor(props) {
    super(props);
    this.uniqueid = getRandomId();
  }

  state = {
    dirty: false,
    visible: false,
    selected: null,
  };

  componentDidMount() {
    const { name, addToForm } = this.props;

    document.addEventListener('click', this.closeIfClickOutside);

    if (addToForm && name) {
      addToForm(name, this.valida(), this.reset);
    }
  }

  componentWillUnmount() {
    const { name, removeFromForm } = this.props;

    document.removeEventListener('click', this.closeIfClickOutside);

    if (removeFromForm && name) {
      removeFromForm(name);
    }
  }

  getTitle = () => {
    const { title, validations } = this.props;
    return getTitle(title, validations);
  };

  isValid = () => this.valida().valid;

  reset = () => {
    this.setState({
      dirty: false,
    });
  };

  clear = () => {
    this.setState({
      selected: null,
    });
  };

  close = (e) => {
    if (e.relatedTarget !== e.target && e.relatedTarget != null) {
      setTimeout(() => { this.setState({ visible: false }); }, 100);
    }
  };

  closeIfClickOutside = (e) => {
    const { visible } = this.state;
    if (!doesNodeContainClick(this.refNode, e) && visible) {
      this.setState({ visible: false });
    }
  };

  valida = () => {
    const {
      name, value, validations, stato,
    } = this.props;
    if (!validations) return { valid: true };
    let contesto;
    if (stato && name) {
      contesto = stato && name && getProp(name, stato);
    } else {
      const { selected } = this.state;
      contesto = value || selected;
    }
    return Validation.validateAll(validations, contesto);
  };

  notifica = (evt, value, additionalData, closeSelect) => {
    const {
      name, onChange, onFormChange, stato, value: valueProp,
    } = this.props;
    this.setState({
      selected: !name && !valueProp ? value : null,
      visible: !closeSelect,
      dirty: true,
    });
    if (onFormChange && name) {
      const obj = {};
      setProp(name, value, obj, stato);
      onFormChange(obj, name, value);
    }
    onChange && onChange(evt, value, additionalData);
  };

  changeVisibility = () => {
    const { visible } = this.state;
    if (this.isActive()) {
      if (!visible) {
        this.lastFocus = true;
      }
      this.setState({ visible: true });
    }
  };

  closeIfOpen = () => {
    if (!this.isActive()) return;
    setTimeout(() => {
      if (this.lastFocus) {
        this.lastFocus = false;
      } else {
        const { visible } = this.state;
        if (!visible) {
          this.setState({ visible: true });
          return;
        }
        this.setState({ visible: false });
      }
    }, 0);
  };

  getValue = () => {
    const { name, value, stato } = this.props;
    let contesto;
    if (name) {
      contesto = stato && name && getProp(name, stato);
      if (Array.isArray(contesto)) {
        contesto = contesto[0]; // eslint-disable-line prefer-destructuring
      }
      if (contesto === '') {
        contesto = null;
      }
    } else {
      const { selected } = this.state;
      contesto = value || selected;
    }
    return contesto;
  };

  chiudiTab = (evt) => {
    if (!this.isActive()) return;
    switch (evt.keyCode) {
      case 9:
        this.setState({ visible: false });
        break;
      default:
        break;
    }
  };

  seleziona = (evt) => {
    const { children } = this.props;
    if (!this.isActive()) return;
    const contestoTxt = `${this.getValue()}`;
    let index = -1;
    React.Children.map(children, (x, i) => {
      if (`${x.props.value}` === contestoTxt || contestoTxt === '') {
        index = i;
      }
    });
    if (index === -1) return;
    switch (evt.keyCode) {
      //  Tab
      case 9:
        if (evt.target !== evt.relatedTarget && evt.relatedTarget != null) {
          this.setState({ visible: false });
        }
        return;
      //  Enter
      case 13:
        this.setState({ visible: false });
        Helpers.focusNextElement(evt.target);
        return;
      //  Arrow up
      case 38:
        evt.preventDefault();
        index -= 1;
        while (index > -1 && index < React.Children.toArray(children).length
          && React.Children.toArray(children)[index].props.value == null) {
          index -= 1;
        }
        break;
      //  Arrow down
      case 40:
        evt.preventDefault();
        index += 1;
        while (index > -1 && index < React.Children.toArray(children).length
          && React.Children.toArray(children)[index].props.value == null) {
          index += 1;
        }
        break;
      default:
        break;
    }
    if (index > -1 && index < React.Children.toArray(children).length) {
      const el = React.Children.toArray(children)[index];
      this.notifica(evt, el.props.value, el.props.additionalData, false);
    }
  };

  scrollOptionIntoView = () => {
    if (this.refSelected == null || this.refList == null) return;
    const menuDOM = this.refList;
    const menuRect = menuDOM.getBoundingClientRect();
    setTimeout(() => {
      const n = findDOMNode(this.refSelected); // eslint-disable-line react/no-find-dom-node
      if (n) {
        const selectedRect = n.getBoundingClientRect();
        if (selectedRect.bottom > (menuRect.bottom - 5) || selectedRect.top < menuRect.top) {
          menuDOM.scrollTop = (n.offsetTop + n.clientHeight - menuDOM.offsetHeight);
        }
      }
    });
  };

  renderMenu = (children) => {
    const { customList } = this.props;
    if (customList) {
      return customList(children);
    }
    return (
      <ul
        className="select__list"
        ref={(r) => { this.refList = r; }}
      >
        {children}
      </ul>
    );
  };

  isActive = () => {
    const { disabled, readOnly } = this.props;
    return !disabled && !readOnly;
  };

  render() {
    const {
      actions,
      children,
      className,
      isValid,
      name,
      right,
      title,
      validare,
      readOnly: readOnlyProp,
      disabled: disabledProp,
      ellipsis,
      placeholder,
    } = this.props;
    const { dirty, visible } = this.state;

    const { disabled, readOnly } = useActivation(disabledProp, readOnlyProp);

    const contesto = this.getValue();
    const contestoTxt = `${contesto}`;
    const validazione = this.valida();
    if (isValid != null && name) {
      isValid(name, validazione.valid, validazione.messages || []);
    }

    const { hasError, hasWarning } = useValidations(validare, dirty, validazione);

    const classes = cns(
      'select input',
      hasError ? 'select--invalid' : 'select--valid',
      {
        'select--right': right,
        'select--disabled': !this.isActive(),
      },
      className,
    );
    const listTitleClasses = cns(
      'select__listTitle',
      {
        'select__listTitle--disabled': disabled,
        'select__listTitle--default': false,
        'select__listTitle--readOnly': readOnly,
      },
    );
    const inputTitleClass = cns('select__title', { 'select__title--readOnly': readOnly });
    const childrenWithProps = React.Children.map(
      children,
      (child) => React.cloneElement(child, {
        notifica: this.notifica,
        ref: (`${child.props.value}` === contestoTxt || contestoTxt === '') ? (r) => { this.refSelected = r; } : () => { },
        value: child.props.value,
        isSelected: `${child.props.value}` === contestoTxt || contestoTxt === '',
      }),
    ) || [];
    const value = childrenWithProps.find((x) => `${x.props.value}` === contestoTxt || contestoTxt === '')
      ? childrenWithProps.find((x) => `${x.props.value}` === contestoTxt || contestoTxt === '').props.children
      : contesto || '';
    const tabIndex = {};
    if (disabled) {
      tabIndex.tabIndex = -1;
    }
    if (visible && childrenWithProps.length > 0 && this.refSelected) {
      this.scrollOptionIntoView();
    }
    const label = name || title;
    return (
      <div className={classes} ref={(r) => { this.refNode = r; }}>
        <div className="select__container">
          <div className="select__wrapper validationViewer__target">
            {readOnly
              ? (
                <span
                  className={listTitleClasses}
                  readOnly={readOnly}
                  disabled={disabled}
                >
                  <EllipsisText {...ellipsis}>
                    {new Presentation({ readOnly }).format(value)}
                  </EllipsisText>
                </span>
              ) : (
                <input
                  id={this.uniqueid}
                  autoComplete={`select-${this.uniqueid}`}
                  className={listTitleClasses}
                  data-origin={name}
                  onClick={this.closeIfOpen}
                  onFocus={this.changeVisibility}
                  onKeyDown={this.chiudiTab}
                  onKeyUp={this.seleziona}
                  disabled={disabled}
                  readOnly={!disabled}
                  value={new Presentation({ readOnly }).format(value)}
                  placeholder={placeholder}
                  {...tabIndex}
                />
              )}
            {label && <label htmlFor={this.uniqueid} className="input__label">{label}</label>}
            {title && <span className={inputTitleClass}>{this.getTitle()}</span>}
            {!readOnly && <span className={!visible ? 'select__listItemArrow' : 'select__listItemArrow close'} />}
            <span className="select__bar" />
            {visible && this.renderMenu(childrenWithProps)}
          </div>
          <ValidationViewer hasError={hasError} hasWarning={hasWarning} validazione={validazione} />
        </div>
        {!readOnly && actions && <div className="select__actions">{actions}</div>}
      </div>
    );
  }
}

Select.defaultProps = {
  className: null,
  name: null,
  onChange: null,
  title: null,
  validations: null,
  value: null,
  disabled: false,
  readOnly: false,
  ellipsis: {
    left: false,
    top: true,
    disabled: false,
  },
  placeholder: undefined,
};

Select.propTypes = {
  ...FormWrapperPropTypes,
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Nome del campo da bindare se il componente è contenuto all'interno di un componente Form */
  name: PropTypes.string,
  /**
   * Funzione invocata alla selezione di un elemento della Select:
   * ```
   *  onChange(event, value, additionalData)
   * ```
   * dove:
   * * *event*: evento click
   * * *value*: valore dell'elemento selezionato
   * * *additionalData*: eventuali dati aggiuntivi associati all'option
   */
  onChange: PropTypes.func,
  /** Etichetta da visualizzare nel campo che renderizza il componente */
  title: PropTypes.string,
  /** Array di validazioni da applicare */
  validations: PropTypes.arrayOf(PropTypes.oneOfType(
    [
      PropTypes.string,
      PropTypes.object,
      PropTypes.func,
    ],
  )),
  /** Valore attribuito al componente se non gestito in una Form (attraverso il *name*).
   * In questa configurazione (senza *name*) per selezionare un determinato *Option* è
   * sufficiente popolare questa prop e attribuirle lo stesso valore indicato nella prop
   * *value* del child *Option*.
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  /** Presenta il campo in modalità disabilitato */
  disabled: PropTypes.bool,
  /** Presenta il campo in modalità read only */
  readOnly: PropTypes.bool,
  /**
Proprietà che definisce il comporamento delle ellipsis nel caso di componente in modalità readOnly.
E' possibile definire se presentatorlo top o bottom, left o right oppure se disabilitare la visualizzazione della tooltip.

Vedi componente EllipsisText.
   */
  ellipsis: PropTypes.shape({
    /** mostra la tooltip nella parte alta/bassa */
    top: PropTypes.bool,

    /** mostra la tooltip nella parte sinistra/destra */
    left: PropTypes.bool,

    /** mostra o nasconde la tooltip */
    disabled: PropTypes.bool,
  }),

  /** Esempio testuale del dato da inserire nel campo */
  placeholder: PropTypes.string,
};

export default Select;
